import React, { useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Download, FileText, Calendar as CalendarIcon } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const reportTypes = [
  { value: 'reservations', label: 'Reservations Report', icon: '📅' },
  { value: 'occupancy', label: 'Occupancy & Performance', icon: '📊' },
  { value: 'maintenance', label: 'Maintenance Report', icon: '🔧' },
  { value: 'guests', label: 'Guests Export', icon: '👥' }
];

const reservationStatuses = ['ALL', 'CONFIRMED', 'IN_HOUSE', 'CHECKED_OUT', 'CANCELLED'];
const maintenanceStatuses = ['ALL', 'OPEN', 'IN_PROGRESS', 'COMPLETED', 'CANCELLED'];

const ExportReports = () => {
  const { selectedProperty } = useOutletContext();
  const [selectedReport, setSelectedReport] = useState('reservations');
  const [exporting, setExporting] = useState(false);
  
  // Report filters
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [reservationStatus, setReservationStatus] = useState('ALL');
  const [maintenanceStatus, setMaintenanceStatus] = useState('ALL');

  const handleExport = async () => {
    if (!selectedProperty) {
      toast.error('Please select a property first');
      return;
    }

    try {
      setExporting(true);
      const token = localStorage.getItem('access_token');
      let url = '';
      
      switch (selectedReport) {
        case 'reservations':
          if (!startDate || !endDate) {
            toast.error('Please select date range');
            return;
          }
          url = `${API}/reports/export/reservations?property_id=${selectedProperty.id}&start_date=${startDate}&end_date=${endDate}&format=csv`;
          if (reservationStatus !== 'ALL') {
            url += `&status=${reservationStatus}`;
          }
          break;
          
        case 'occupancy':
          if (!startDate || !endDate) {
            toast.error('Please select date range');
            return;
          }
          url = `${API}/reports/export/occupancy?property_id=${selectedProperty.id}&start_date=${startDate}&end_date=${endDate}&format=csv`;
          break;
          
        case 'maintenance':
          url = `${API}/reports/export/maintenance?property_id=${selectedProperty.id}&format=csv`;
          if (startDate && endDate) {
            url += `&start_date=${startDate}&end_date=${endDate}`;
          }
          if (maintenanceStatus !== 'ALL') {
            url += `&status=${maintenanceStatus}`;
          }
          break;
          
        case 'guests':
          url = `${API}/reports/export/guests?format=csv`;
          break;
          
        default:
          toast.error('Invalid report type');
          return;
      }

      const response = await axios.get(url, {
        headers: { 
          Authorization: `Bearer ${token}`,
        },
        responseType: 'blob'
      });

      // Create download link
      const blob = new Blob([response.data], { type: 'text/csv' });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `${selectedReport}_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);

      toast.success('Report exported successfully');
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export report');
    } finally {
      setExporting(false);
    }
  };

  if (!selectedProperty) {
    return (
      <div className="flex items-center justify-center h-full">
        <p className="text-slate-600">Please select a property</p>
      </div>
    );
  }

  const selectedReportInfo = reportTypes.find(r => r.value === selectedReport);

  return (
    <div className="space-y-6" data-testid="export-reports-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Export Reports</h1>
          <p className="text-slate-600 mt-1">Download data for analysis and accounting</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-1">
          <CardHeader>
            <CardTitle className="text-lg">Report Type</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {reportTypes.map((report) => (
              <button
                key={report.value}
                onClick={() => setSelectedReport(report.value)}
                className={`w-full text-left px-4 py-3 rounded-lg transition-all flex items-center gap-3 ${
                  selectedReport === report.value
                    ? 'bg-gradient-to-r from-cyan-50 to-blue-50 border-2 border-cyan-500'
                    : 'bg-slate-50 hover:bg-slate-100 border-2 border-transparent'
                }`}
              >
                <span className="text-2xl">{report.icon}</span>
                <span className={`font-medium ${
                  selectedReport === report.value ? 'text-cyan-700' : 'text-slate-700'
                }`}>
                  {report.label}
                </span>
              </button>
            ))}
          </CardContent>
        </Card>

        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <span className="text-2xl">{selectedReportInfo?.icon}</span>
              {selectedReportInfo?.label}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Date Range - For most reports */}
            {selectedReport !== 'guests' && (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Start Date {selectedReport !== 'maintenance' && '*'}</Label>
                  <Input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    max={endDate || undefined}
                  />
                </div>
                <div>
                  <Label>End Date {selectedReport !== 'maintenance' && '*'}</Label>
                  <Input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    min={startDate || undefined}
                  />
                </div>
              </div>
            )}

            {/* Reservations specific filters */}
            {selectedReport === 'reservations' && (
              <div>
                <Label>Reservation Status</Label>
                <Select value={reservationStatus} onValueChange={setReservationStatus}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {reservationStatuses.map(status => (
                      <SelectItem key={status} value={status}>{status}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Maintenance specific filters */}
            {selectedReport === 'maintenance' && (
              <div>
                <Label>Work Order Status</Label>
                <Select value={maintenanceStatus} onValueChange={setMaintenanceStatus}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {maintenanceStatuses.map(status => (
                      <SelectItem key={status} value={status}>{status}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Report description */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-sm text-blue-900">
                {selectedReport === 'reservations' && 
                  'Export all reservations with guest details, room assignments, and financial information.'}
                {selectedReport === 'occupancy' && 
                  'Export daily occupancy rates, ADR, RevPAR, and revenue metrics for the selected date range.'}
                {selectedReport === 'maintenance' && 
                  'Export all work orders with status, priority, costs, and completion details.'}
                {selectedReport === 'guests' && 
                  'Export complete guest database with contact information and notes.'}
              </p>
            </div>

            {/* Export button */}
            <Button
              onClick={handleExport}
              disabled={exporting}
              className="w-full gap-2"
              size="lg"
            >
              {exporting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                  Exporting...
                </>
              ) : (
                <>
                  <Download size={18} />
                  Export as CSV
                </>
              )}
            </Button>

            <div className="text-xs text-slate-500 text-center">
              CSV format is compatible with Excel, Google Sheets, and most data analysis tools
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText size={20} />
            Available Reports
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {reportTypes.map((report) => (
              <div key={report.value} className="flex items-start gap-3 p-4 bg-slate-50 rounded-lg">
                <span className="text-2xl">{report.icon}</span>
                <div>
                  <h3 className="font-medium text-slate-900">{report.label}</h3>
                  <p className="text-sm text-slate-600 mt-1">
                    {report.value === 'reservations' && 'Booking history and guest stays'}
                    {report.value === 'occupancy' && 'Revenue and occupancy analytics'}
                    {report.value === 'maintenance' && 'Work order tracking and costs'}
                    {report.value === 'guests' && 'Complete guest database'}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ExportReports;
